import React, { useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Alert, Button, Card, Modal, Spinner, Table } from 'react-bootstrap';
import { VANITY_ITEM_OWNERSHIP_STATS } from './graphql';

export type VanityCategory = 'STICKER' | 'APP_SKIN' | 'PROFILE_FRAME' | 'POSTER' | 'BADGE' | 'EMOJI_PACK';

type Props = {
  category: VanityCategory;
  itemId: string;
};

const OwnershipStatsCard = ({ category, itemId }: Props) => {
  const baseId = itemId.split('.')[0];
  const [showConfirm, setShowConfirm] = useState(false);

  const [runQuery, { data, loading, error, called }] = useLazyQuery(VANITY_ITEM_OWNERSHIP_STATS, {
    fetchPolicy: 'network-only',
  });

  const stats = data?.vanityItemOwnershipStats;
  const stale = stats && (stats.vanityItemIdPrefix !== baseId || stats.category !== category);

  const handleConfirm = () => {
    setShowConfirm(false);
    runQuery({ variables: { category, vanityItemIdPrefix: baseId } });
  };

  return (
    <Card className="mt-3">
      <Card.Header>
        <span className="font-weight-bold">
          <i className="fas fa-users mr-2" />
          Vanity Ownership Stats
        </span>
      </Card.Header>
      <Card.Body>
        <Alert variant="info" className="py-2">
          <i className="fas fa-info-circle mr-2" />
          Counts how many users own this item (any level), and how many of them have been
          active in the last 30 days.
        </Alert>

        <Table bordered size="sm" className="mb-3">
          <tbody>
            <tr>
              <th style={{ width: 220 }}>Item ID</th>
              <td><code>{baseId}</code></td>
            </tr>
            <tr>
              <th>Total users owning</th>
              <td>
                {loading ? (
                  <span className="text-muted">
                    <Spinner as="span" animation="border" size="sm" className="mr-2" />
                    Counting...
                  </span>
                ) : stats && !stale ? (
                  stats.totalUsers.toLocaleString()
                ) : (
                  <span className="text-muted">Not yet counted</span>
                )}
              </td>
            </tr>
            <tr>
              <th>Active in last 30 days</th>
              <td>
                {loading ? (
                  <span className="text-muted">
                    <Spinner as="span" animation="border" size="sm" className="mr-2" />
                    Counting...
                  </span>
                ) : stats && !stale ? (
                  stats.activeLast30Days.toLocaleString()
                ) : (
                  <span className="text-muted">Not yet counted</span>
                )}
              </td>
            </tr>
            <tr>
              <th>Computed at</th>
              <td>
                {loading ? (
                  <span className="text-muted">—</span>
                ) : stats && !stale ? (
                  new Date(stats.computedAt).toLocaleString()
                ) : (
                  <span className="text-muted">—</span>
                )}
              </td>
            </tr>
          </tbody>
        </Table>

        {error && (
          <Alert variant="danger" className="py-2">
            {error.message}
          </Alert>
        )}

        <Alert variant="warning" className="py-2">
          <i className="fas fa-exclamation-triangle mr-2" />
          <b>Expensive operation</b> (~1-3 min) — please run only when needed.
        </Alert>
        <Button onClick={() => setShowConfirm(true)} disabled={loading || !itemId} variant="primary">
          {loading ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="mr-2" />
              Running (this can take a minute)...
            </>
          ) : called ? (
            'Re-run Stats'
          ) : (
            'Get Stats'
          )}
        </Button>
      </Card.Body>

      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-exclamation-triangle text-warning mr-2" />
            Heads up — expensive operation
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This is expensive on the database (~1-3 min). Please run only when needed — once per
          item is usually enough.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirm}>
            Run anyway
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default OwnershipStatsCard;
